import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useIsTouchDevice } from '@bluheadless/ui-logic/src/hooks/useIsTouchDevice'
import { cx } from '@emotion/css'
import { arrayOf, bool, func, object, shape, string } from 'prop-types'
import React, { useRef, useState, useEffect } from 'react'
import Image from '../../particles/image'
import Slider, { Slide } from '../../particles/slider'
import MouseMoveZoom from './mouse-move-zoom'
import { DialogStyled, SliderThumbsWrapper } from './zoom.styled'
import SwiperZoom from './swiper-zoom'

const Zoom = React.memo(
	function Zoom({
		className,
		currentImage,
		images,
		mouseMoveZoomProps,
		open,
		setToggleZoom,
		swiperProps,
		thumbsProps,
		...props
	}) {
		const isTouch = useIsTouchDevice()
		const sliderRef = useRef(null)
		const [thumbsSwiper, setThumbsSwiper] = useState()

		useEffect(() => {
			if ((!open || images?.length <= 1) && thumbsSwiper) {
				setThumbsSwiper()
			}
		}, [images, thumbsSwiper, open])

		const {
			catalog: {
				products: {
					zoom: {
						thumbs: { enabled: thumbsEnabled, width: thumbsWidth, height: thumbsHeight },
					},
				},
			},
		} = useConfig()

		return (
			<DialogStyled
				className={cx('Zoom-root', className)}
				fullScreen
				maxWidth={false}
				onClose={() =>
					setToggleZoom({ open: false, currentImage: images[sliderRef?.current?.swiper?.activeIndex]?.image })
				}
				open={open}
				{...props}
			>
				{images?.length > 1 ? (
					<>
						<Slider
							containerProps={{ sx: { height: '100%' } }} // for arrow navigation's position
							initialSlide={currentImage ? images.findIndex((image) => image.image === currentImage) : 0}
							ref={sliderRef}
							subContainerProps={{ sx: { height: '100%' } }} // for arrow navigation's position
							thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
							{...swiperProps}
						>
							{images.map(({ image, alt }, i) => (
								<Slide key={i}>
									{!isTouch ? (
										<MouseMoveZoom setToggleZoom={setToggleZoom} image={image} alt={alt} {...mouseMoveZoomProps} />
									) : (
										<SwiperZoom image={image} alt={alt} />
									)}
								</Slide>
							))}
						</Slider>

						{thumbsEnabled && (
							<SliderThumbsWrapper>
								<Slider
									initialSlide={currentImage ? images.findIndex((image) => image.image === currentImage) : 0}
									onSwiper={setThumbsSwiper}
									{...thumbsProps}
								>
									{images.map(({ image, alt }, i) => (
										<Slide key={i}>
											<Image alt={alt} originalHeight={thumbsHeight} originalWidth={thumbsWidth} src={image} />
										</Slide>
									))}
								</Slider>
							</SliderThumbsWrapper>
						)}
					</>
				) : !isTouch ? (
					<MouseMoveZoom
						setToggleZoom={setToggleZoom}
						image={images[0]?.image}
						alt={images[0]?.alt}
						{...mouseMoveZoomProps}
					/>
				) : (
					<SwiperZoom image={images[0]?.image} alt={images[0]?.alt} />
				)}
			</DialogStyled>
		)
	},
	(prevProps, nextProps) => {
		return prevProps.currentImage === nextProps.currentImage && prevProps.open === nextProps.open
	}
)

Zoom.defaultProps = {
	maxWidth: false,
	showButtonClose: true,
	swiperProps: {
		slidesPerView: 1,
		spaceBetween: 0,
		speed: 300,
		effect: 'slide',
		navigation: true,
		zoom: true,
	},
	thumbsProps: {
		slidesPerView: 3,
		spaceBetween: 20,
		slideToClickedSlide: true,
	},
}

Zoom.propTypes = {
	open: bool.isRequired,
	setToggleZoom: func.isRequired,
	currentImage: string,
	images: arrayOf(object).isRequired,
	thumbsProps: object,
	swiperProps: shape(Slider.propTypes),
	mouseMoveZoomProps: object,
}

export default Zoom
